import { forwardRef, HTMLProps } from 'react';
import {
  StyledDescription,
  StyledInput,
  StyledInputHorizontalContainer,
  StyledInputPrefix,
  StyledInputWrapper,
} from './Input.styles';

export interface InputProps extends HTMLProps<HTMLInputElement> {
  /** Primary label for the input */
  label: string;
  /** ID for the input */
  id: string;
  /** Optional description for the input, appears below the label */
  description?: string | JSX.Element;
  /** Props to pass to the label element */
  labelProps?: HTMLProps<HTMLLabelElement>;
  /** Optional prefix for the input */
  prefix?: string;
  /** layout (horizontal/vertical) */
  layout?: 'horizontal' | 'vertical';
  /** Whether the input should be smaller/compacted */
  compact?: boolean;
  role?: string;
}

export type InputRef = HTMLInputElement;

/**
 * A thin wrapper around a <label>; and <input> component. All props will be passed
 * to the input except for the following:
 *
 * * label
 * * labelProps
 * * description
 *
 * IDs and aria attributes for the label (and optional description) will be set
 * automatically based on the provided ID.
 * Note that these have the same backgrounda as the default fill background so you
 * will need to use them inside something like a <Card> component
 *
 * TODO: other states e.g. errors, disabled etc
 */
export const Input = forwardRef<InputRef, InputProps>(
  (
    {
      label,
      id,
      description,
      labelProps,
      prefix,
      compact,
      layout = 'vertical',
      role,
      ...otherProps
    },
    ref,
  ) => {
    if (otherProps.type === 'radio') {
      throw new Error(
        'This component does not support radio inputs. Use the <Radio /> component instead',
      );
    }
    if (otherProps.type === 'checkbox') {
      throw new Error(
        'This component does not support checkboxes. Use the <Checkbox /> component instead',
      );
    }

    if (layout === 'horizontal') {
      return (
        <StyledInputHorizontalContainer>
          <div role={role}>
            <label id={`${id}-label`} htmlFor={id} {...labelProps}>
              {label}
            </label>
            {description && (
              <StyledDescription id={`${id}-description`}>
                {description}
              </StyledDescription>
            )}
          </div>
          <StyledInputWrapper>
            {prefix && <StyledInputPrefix>{prefix}</StyledInputPrefix>}
            <StyledInput
              id={id}
              ref={ref}
              $compact={compact}
              aria-labelledby={`${id}-label`}
              aria-describedby={description ? `${id}-description` : undefined}
              {...otherProps}
            />
          </StyledInputWrapper>
        </StyledInputHorizontalContainer>
      );
    }
    return (
      <div role={role}>
        <label id={`${id}-label`} htmlFor={id} {...labelProps}>
          {label}
        </label>
        {description && (
          <StyledDescription id={`${id}-description`}>
            {description}
          </StyledDescription>
        )}
        <StyledInputWrapper>
          {prefix && <StyledInputPrefix>{prefix}</StyledInputPrefix>}
          <StyledInput
            id={id}
            ref={ref}
            $compact={compact}
            aria-labelledby={`${id}-label`}
            aria-describedby={description ? `${id}-description` : undefined}
            {...otherProps}
          />
        </StyledInputWrapper>
      </div>
    );
  },
);

export default Input;
